// src/components/kuestion/campus/CampusContext.jsx
//
// Signed-in user state for Kuestion: the account mode (v3 §1.1), and for a
// Campus account the organisation, the roster, the templates and the
// assignments, each loaded once and refreshed on demand, plus the background
// PDF report jobs (§7.1, asynchronous per the backend response A5).
// Everything goes through useKuestionApi, i.e. the user's token.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useAuth } from '../../../context/AuthContext';
import { useKuestionApi, KuestionError } from '../useKuestionApi';
import { modeClaim } from '../shared/tokenClaims';

const CampusCtx = createContext(null);

// Poll cadence for a report job that is queued or building.
const REPORT_POLL_MS = 5000;
const REPORT_POLL_MAX = 60; // 5 minutes

export const CampusProvider = ({ children }) => {
  const api = useKuestionApi();
  const { user, refreshToken } = useAuth();

  // Account mode: 'loading' → 'choose' (null from the server) → 'solo' | 'campus'
  const [modeState, setModeState] = useState('loading');
  const [modeInfo, setModeInfo] = useState(null); // { mode, chosen_at, has_org }
  const [modeError, setModeError] = useState('');

  // Campus only: 'idle' → 'loading' → 'none' (wizard) → 'ready'
  const [orgState, setOrgState] = useState('idle');
  const [org, setOrg] = useState(null);
  const [orgError, setOrgError] = useState('');

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  const loadMode = useCallback(async () => {
    setModeError('');
    try {
      const data = await api.get(API_ENDPOINTS.CAMPUS_MODE);
      const m = data?.mode;
      setModeInfo(data || null);
      if (m === 'solo' || m === 'campus') setModeState(m);
      else setModeState('choose');
    } catch (e) {
      // A missing route means a server that predates v3: everything is
      // Campus, as before. Any other failure is shown with a retry.
      if (e instanceof KuestionError && e.status === 404) setModeState('campus');
      else { setModeError(e.message); setModeState('error'); }
    }
  }, [api]);

  // Order of authority (backend reply 17 Sep §4):
  //   1. the signed `mode` claim in the token. A chosen mode is permanent, so
  //      "solo" or "campus" there can never be stale: route with no call.
  //      This also keeps a Solo user in Solo when the mode route is down.
  //   2. the mode the login response carried (§9.0), same reasoning.
  //   3. otherwise ask the server. That covers a claim that is absent (a
  //      token older than the claim: unknown, not null, B1) and a claim that
  //      is null: the account may have chosen since this token was minted,
  //      and asking again would only earn mode_already_set.
  //   4. only if the route is missing does loadMode fall back.
  useEffect(() => {
    if (!api.isReady) return;
    const claim = modeClaim(user?.token);
    const m = claim.mode || user?.accountMode;
    if (m === 'solo' || m === 'campus') { setModeInfo({ mode: m }); setModeState(m); return; }
    loadMode();
  }, [api.isReady, user?.token, user?.accountMode, loadMode]);

  // PUT once. `mode_already_set` is final — re-read what the server holds
  // rather than offering a retry.
  const chooseMode = useCallback(async (mode) => {
    try {
      const data = await api.putJson(API_ENDPOINTS.CAMPUS_MODE, { mode });
      setModeInfo({ mode, chosen_at: data?.chosen_at || new Date().toISOString(), has_org: false });
      setModeState(mode);
      // `token` is optional: absent means no refresh was available, and the
      // stale null claim is then settled by GET /campus/mode on next load.
      if (data?.token) refreshToken(data.token, mode);
    } catch (e) {
      // The 409 heals itself: it carries the mode the account really has and a
      // token whose claim agrees. Without them, ask the server as before.
      if (e.reason === 'mode_already_set') {
        // Payload in `data`, as on every error that carries one; the top level
        // is the older copy of the same two fields.
        const carried = e.body?.data || e.body || {};
        const current = carried.current_mode;
        if (carried.token && (current === 'solo' || current === 'campus')) {
          refreshToken(carried.token, current);
          setModeInfo({ mode: current });
          setModeState(current);
        } else { await loadMode(); }
      }
      throw e;
    }
  }, [api, loadMode, refreshToken]);

  const loadOrg = useCallback(async () => {
    setOrgError('');
    setOrgState((s) => (s === 'idle' ? 'loading' : s));
    try {
      const data = await api.get(API_ENDPOINTS.CAMPUS_ORG);
      if (data && data.prefix) {
        setOrg(data);
        setOrgState('ready');
      } else {
        setOrg(null);
        setOrgState('none');
      }
    } catch (e) {
      if (e instanceof KuestionError && (e.reason === 'org_not_found' || e.status === 404)) {
        setOrg(null);
        setOrgState('none');
      } else if (e instanceof KuestionError && e.reason === 'mode_not_set') {
        // The token said Campus but the account never chose — send them to
        // the selection screen, not an error (contract §9.0).
        setModeState('choose');
      } else if (e instanceof KuestionError && e.reason === 'not_campus_account') {
        setModeInfo({ mode: 'solo' });
        setModeState('solo');
      } else {
        setOrgError(e.message);
        setOrgState('error');
      }
    }
  }, [api]);

  useEffect(() => { if (modeState === 'campus') loadOrg(); }, [modeState, loadOrg]);

  // The wizard hands over the org it just created; committing it is what
  // swaps the wizard for the shell.
  const commitOrg = useCallback((o) => {
    setOrg(o);
    setOrgState('ready');
    setModeInfo((m) => ({ ...(m || {}), has_org: true }));
  }, []);

  const reloadStudents = useCallback(async () => {
    setStudentsLoading(true);
    try {
      const data = await api.get(API_ENDPOINTS.CAMPUS_STUDENTS);
      setStudents(Array.isArray(data) ? data : (data?.students || []));
    } catch (e) {
      console.error('students:', e);
    } finally {
      setStudentsLoading(false);
    }
  }, [api]);

  const reloadTemplates = useCallback(async () => {
    try {
      setTemplates(await api.get(API_ENDPOINTS.KQ_TEMPLATES) || []);
    } catch (e) {
      console.error('templates:', e);
    }
  }, [api]);

  const reloadAssignments = useCallback(async () => {
    setAssignmentsLoading(true);
    try {
      const data = await api.get(API_ENDPOINTS.CAMPUS_ASSIGNMENTS);
      // Item 3 names (test access note, 17 Sep): counts are per student, and
      // expired_count is a subset of submitted_count. Screens keep reading the
      // v4 names, filled from whichever the server sent.
      setAssignments((Array.isArray(data) ? data : (data?.assignments || [])).map((a) => ({
        ...a,
        id: a.id ?? a.assignment_id,
        template_name: a.title || a.template_name,
        student_count: a.assigned_count ?? a.student_count,
        completed_count: a.submitted_count ?? a.completed_count,
      })));
    } catch (e) {
      console.error('assignments:', e);
    } finally {
      setAssignmentsLoading(false);
    }
  }, [api]);

  // First load of everything the Campus dashboard needs, once the org exists.
  useEffect(() => {
    if (orgState !== 'ready') return;
    reloadStudents();
    reloadTemplates();
    reloadAssignments();
  }, [orgState, reloadStudents, reloadTemplates, reloadAssignments]);

  // ── PDF report jobs ────────────────────────────────────────────────
  // { key, label, jobId, status: 'queued'|'building'|'ready'|'failed', url, error, at }
  const [reports, setReports] = useState([]);
  const timers = useRef({});

  useEffect(() => () => { Object.values(timers.current).forEach(clearTimeout); }, []);

  const patchReport = useCallback((key, patch) => {
    setReports((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }, []);

  // POST creates the job; GET /jobs/{id} is polled until ready or failed.
  // The `url` on ready is a short-lived signed link — hand it to the browser.
  const requestReport = useCallback((key, label, postUrl) => {
    setReports((rs) => {
      const existing = rs.find((r) => r.key === key);
      if (existing && (existing.status === 'queued' || existing.status === 'building')) return rs;
      return [{ key, label, jobId: null, url: null, status: 'queued', at: Date.now() }, ...rs.filter((r) => r.key !== key)];
    });

    let attempts = 0;
    const poll = async (jobId) => {
      attempts += 1;
      try {
        const job = await api.get(API_ENDPOINTS.CAMPUS_REPORT_JOB(jobId));
        const status = job?.status || 'building';
        if (status === 'ready') { patchReport(key, { status: 'ready', url: job.url, at: Date.now() }); return; }
        if (status === 'failed') { patchReport(key, { status: 'failed', error: job?.error || 'The report could not be built. Try again.' }); return; }
        patchReport(key, { status });
        if (attempts >= REPORT_POLL_MAX) { patchReport(key, { status: 'failed', error: 'The report is taking too long. Try again later.' }); return; }
        timers.current[key] = setTimeout(() => poll(jobId), REPORT_POLL_MS);
      } catch (e) {
        if (e.reason === 'report_not_ready') { timers.current[key] = setTimeout(() => poll(jobId), REPORT_POLL_MS); return; }
        patchReport(key, { status: 'failed', error: e.message });
      }
    };

    (async () => {
      try {
        const data = await api.postJson(postUrl, {});
        const jobId = data?.job_id ?? data?.id;
        if (!jobId) throw new KuestionError('server_error', 500);
        patchReport(key, { jobId, status: data?.status || 'queued' });
        if (data?.status === 'ready' && data?.url) { patchReport(key, { status: 'ready', url: data.url, at: Date.now() }); return; }
        poll(jobId);
      } catch (e) {
        patchReport(key, { status: 'failed', error: e.message });
      }
    })();
  }, [api, patchReport]);

  const value = useMemo(() => ({
    api,
    user,
    modeState, modeInfo, modeError, reloadMode: loadMode, chooseMode,
    org, setOrg: commitOrg, orgState, orgError, reloadOrg: loadOrg,
    students, studentsLoading, reloadStudents,
    templates, reloadTemplates,
    assignments, assignmentsLoading, reloadAssignments,
    reports, requestReport,
  }), [api, user, modeState, modeInfo, modeError, loadMode, chooseMode, org, commitOrg, orgState, orgError, loadOrg,
    students, studentsLoading, reloadStudents, templates, reloadTemplates, assignments, assignmentsLoading,
    reloadAssignments, reports, requestReport]);

  return <CampusCtx.Provider value={value}>{children}</CampusCtx.Provider>;
};

export const useCampus = () => {
  const ctx = useContext(CampusCtx);
  if (!ctx) throw new Error('useCampus must be used within CampusProvider');
  return ctx;
};
